import * as angular from 'angular';
import * as d3 from 'd3';

import { AlarmsHeatMapWidget } from './alarmsHeatMap.widget';
import { ComponentDefinition, Group, Dashboard, Dictionary } from '../../models';
import { GroupAlarmSummary } from '../../models/Dashboards';
import { StoreElement, Store } from '../../stateManagement';
import { StoreService, IDateRangeService } from '../../services';
import { Unsubscribe } from 'redux';
import { errorTextColor, infoTextColor, warningTextColor, whiteTextColor } from '../../common/constants';

interface HeatMapRow {
    critical: HeatMapCell;
    group: Group;
    groupGuid: string;
    information: HeatMapCell;
    warning: HeatMapCell;
}

interface HeatMapCell {
    count: number;
    color: string;
}

class AlarmsHeatMapController implements angular.IComponentController {
    beginDate: string;
    boundData: {
        guid: string;
        summary: GroupAlarmSummary[];
    };
    cols = {
        hasWarn: true,
        hasInfo: true,
        hasCrit: true,
    };
    dashboardsDic: Dictionary<Dashboard>;
    endDate: string;
    isLoading: boolean;
    isEditMode: boolean = false;
    item: AlarmsHeatMapWidget;
    rows: HeatMapRow[];

    private _groups: Dictionary<Group>;
    private _scales: {
        info: d3.scale.Linear<string, string>;
        warn: d3.scale.Linear<string, string>;
        crit: d3.scale.Linear<string, string>;
    };
    private _storeLoaded: angular.IPromise<void>;
    private _unsubscribe: Unsubscribe;

    static $inject = [
        '$q',
        'storeService',
        'dateRangeService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _storeService: StoreService,
        private _dateRangeService: IDateRangeService
    ) {
        // onchange is called before onInit, thus we need to do it here in constructor
        // we use store to handle both edition and dashboard display of widget
        // in best case editing widget should allow to pass directly the list of groups
        const defer = this._$q.defer<void>();
        this._storeLoaded = defer.promise;
        this.isLoading = true;
        this._unsubscribe = this._storeService.register(
            store => this.initStore(store, defer),
            [
                StoreElement.Groups,
                StoreElement.Dashboards
            ]
        );
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    $onChanges(changes: angular.IOnChangesObject) {
        this._storeLoaded.then(() => this.refresh());
    }

    private initStore(store: Store, defer: angular.IDeferred<void>): void {
        this._groups = store.groups.dictionary;
        this.dashboardsDic = store.dashboards.dictionary;
        defer.resolve();
    }

    private updateDisplayOptions() {
        const sev = this.item.configuration.severities;
        const hasSev = sev?.length > 0;
        this.cols.hasInfo = !hasSev || sev.indexOf('Information') >= 0;
        this.cols.hasWarn = !hasSev || sev.indexOf('Warning') >= 0;
        this.cols.hasCrit = !hasSev || sev.indexOf('Critical') >= 0;
    }

    private updateColorScales() {
        const isColor = this.item.configuration.isColoring;
        const max = this.item.configuration.coloringScale ?? 10;
        this._scales = {
            info: d3.scale.linear<string, string>().interpolate(d3.interpolateRgb).domain([0, max]).range([whiteTextColor, isColor ? infoTextColor : whiteTextColor]).clamp(true),
            warn: d3.scale.linear<string, string>().interpolate(d3.interpolateRgb).domain([0, max]).range([whiteTextColor, isColor ? warningTextColor : whiteTextColor]).clamp(true),
            crit: d3.scale.linear<string, string>().interpolate(d3.interpolateRgb).domain([0, max]).range([whiteTextColor, isColor ? errorTextColor : whiteTextColor]).clamp(true),
        };        
    }

    private updateRows() {
        if (this.boundData && this.boundData.summary) {
            const rows = this.boundData.summary.map(sum => {
                const crit = sum.counts['Critical'];
                const warn = sum.counts['Warning'];
                const info = sum.counts['Information'];
                return {
                    groupGuid: sum.guid,
                    group: this._groups[sum.guid],
                    critical: { count: crit, color: this._scales.crit(crit) },
                    warning: { count: warn, color: this._scales.warn(warn) },
                    information: { count: info, color: this._scales.info(info) }
                } as HeatMapRow;
            });

            this.rows = rows;
        }
    }

    private refresh() {
        this.beginDate = this._dateRangeService.beginDate() as string;
        this.endDate = this._dateRangeService.endDate() as string;
        this.updateDisplayOptions();
        this.updateColorScales();
        this.updateRows();
        this.isLoading = false;
    }
}

export const wtAlarmsHeatMap: ComponentDefinition = {
    componentId: 'wtAlarmsHeatMap',
    controller: AlarmsHeatMapController,
    template: require('./alarmsHeatMap.component.html'),
    controllerAs: 'vm',
    bindings: {
        item: '<',
        boundData: '<?',
        isEditMode: '<?'
    }
};